import { get as safeGet, orderBy } from 'lodash'
import { productUtils, context, request, salesforceConnectionMonkeyPatch } from '@tools/wr-catalog-base'
import routes from '@routes/routes'
import { parseUserFromToken } from '@utils/token'
import theme from './theme'
import jsforce from 'jsforce'
import { isPartnerExperience } from '@utils/isPartnerExperience'

const normalizeEnvVar = (v) => String(v || '').replace(/^(none|empty)$/, '')

const catalogsIdsEnv = process.env.REACT_APP_WR_CATALOG_ID || '2938a723-4124-4139-978d-2900237d9499'
const catalogIds = catalogsIdsEnv.trim().split(/\s*,\s*/)
const partnerCatalogId = normalizeEnvVar(process.env.REACT_APP_WR_PARTNER_CATALOG_ID)
const partnerCatalogHost = normalizeEnvVar(process.env.REACT_APP_WR_PARTNER_INSTANCE)

const existingUser = parseUserFromToken(context.persistentStorage.get('ptcToken'))

if (existingUser && existingUser.email) {
  const uid = existingUser.email.replace(/@.+/, '')
  const exp = new Date()
  exp.setFullYear(exp.getFullYear() + 1)
  document.cookie = `uid=${uid}; expires=${exp.toUTCString()}; path=/`
  window.s_props = {
    prop1: uid,
  }
}

if (window.insertPTCTracking) {
  window.insertPTCTracking()
}

const sortingOptions = [
  {
    id: 1,
    label: 'A-Z',
    name: 'A-Z',
    value: 'a_z',
  },
  {
    id: 2,
    label: 'Z-A',
    name: 'Z-A',
    value: 'z_a',
  },
  {
    id: 3,
    label: 'Newly created',
    value: 'newlyCreated',
    func: (products) => orderBy(products, ['createdAt'], ['desc']),
  },
  {
    id: 4,
    label: 'Last modified',
    name: 'Last modified',
    value: 'last_modified',
  },
]

export default {
  state: {
    isLoggedIn: Boolean(context.persistentStorage.get('ptcToken')),
    user: existingUser,
    pointSystem: true,
    selectedCatalogId: catalogIds[0],
    isCatalogExportLoading: false,
    allRegions: [],
    selectedRegion: '',
    addOnItems: {},
    activeSorting: sortingOptions.find((opt) => String(opt.id) === localStorage.activeSorting) || sortingOptions[0],
    savedCategories: {},
    partnerCatalogItemsInfo: [],
    ...(process.env.NODE_ENV === 'development' && localStorage.wrLocalDebugState
      ? JSON.parse(localStorage.wrLocalDebugState)
      : {}),
  },
  theme,
  constants: {
    isSsoDisabled: process.env.REACT_APP_WR_USES_SAML_SSO !== 'true',
    requireSsoAuth: isPartnerExperience(),
    sortingOptions,
    cart: {
      summaryDescription: true,
      multiple: false,
    },
    product: {
      list: {
        gridColSizes: { sm: 6, md: 6, lg: 4 },
      },
      details: {
        displayItemRowHint: true,
      },
    },
    sso: {
      publicUrl: process.env.PUBLIC_URL,
      host: process.env.REACT_APP_SSO_HOST,
      newWindowFlow: true,
      instanceUrl: process.env.REACT_APP_WR_SSO_INSTANCE_URL,
      onAuthResolved: ({ authorization }) => {
        const sfConn = new jsforce.Connection({
          instanceUrl: 'https://na123.lightning.force.com',
          proxyUrl: `${context.constants.sso.host || context.constants.appHost}/api/v1/sso/act`,
        })

        // FIXME: Workaround
        if (context.state.user) context.state.user.id = undefined

        salesforceConnectionMonkeyPatch(sfConn, { authorization })
        context.constants.getSfConn = () => sfConn
        Object.assign(window, { sfConn })
      },
    },
    token: process.env.REACT_APP_WR_ACCESS_TOKEN || '04e734df-cd33-48bb-9cfc-2cff4804a550',
    transactionsEnabled: process.env.NODE_ENV !== 'development',
    host: process.env.REACT_APP_WR_APP_HOST || 'https://sanity.workrails.com',
    appHost: process.env.REACT_APP_WR_APP_HOST || 'https://sanity.workrails.com',
    client: {
      restrictLogin: true,
      emailDomains: ['@ptc.com'],
      companyId: process.env.REACT_APP_WR_COMPANY_ID || '71392fbb-9ae5-4e45-ad2a-6511ac1ae445',
      catalogIds,
      partnerCatalogId,
      partnerCatalogHost,
    },
  },
  utils: {
    getRegionName: () => {
      return context.state.selectedRegion
    },
    proposals: {
      requests: {
        fetchProposals: {
          processRequestParams: (params) => {
            delete params.query.userId
            params.query.companyId = context.constants.client.companyId
            params.query['settings.email'] = context.state.user.email
            return params
          },
        },
        createPDFRequest: ({ html }) =>
          request({
            url: `${context.constants.appHost}/api/v1/documents/export`,
            method: 'POST',
            body: JSON.stringify({
              data: {
                html,
                options: {
                  media: 'print',
                  appendPageNumbers: true,
                  pageNumberOptions: {
                    dx: 60,
                    dy: 12,
                    // --color-green: #68c251
                    color: [104, 194, 81].map((c) => c / 255),
                    skipPages: [0],
                  },
                },
              },
            }),
          }),
        createDocxRequest: ({ html }) =>
          request({
            url: `${context.constants.appHost}/api/v1/documents/export?format=docx:libreoffice`,
            method: 'POST',
            body: JSON.stringify({
              data: {
                html,
              },
            }),
          }),
      },
    },
    product: {
      getPointPrice: (product) => {
        const {
          utils: { getRegionName },
        } = context

        let points = (() => {
          const pricingRow = (product.rows || []).find((r) => r.rowType === 'component:pricing')
          if (!pricingRow) return 0

          const regionName = getRegionName()
          if (!regionName) return Number(safeGet(pricingRow.control, 'schedule.serviceRateLabel')) || 0

          const region = pricingRow.control.constants.find((cons) => cons.name === regionName)
          if (!region) return 0

          return region.value / 100
        })()

        if (product.addOnConfig) {
          points = Math.round(productUtils.getDiscountedAddOnPrice(points, product.addOnConfig))
        }
        return points
      },
      request: {
        postTransformProduct: (item) => {
          item.rows.forEach((row) => {
            if (row.rowType === 'documents' && Array.isArray(row.control)) {
              row.control.forEach((doc) => {
                try {
                  if (typeof doc.settings === 'string') {
                    doc.settings = JSON.parse(doc.settings)
                  }
                } catch (e) {
                  console.error(e)
                }
              })
            }
          })
        },
      },
    },
  },
  routes,
}
